import { Entity } from '@rest-hooks/rest';

// Primary entity used for all request-based resources.
// Provides fetch helpers and a placeholder getFetchOptions() to retain legacy API expectations.
class RequestEntity extends Entity {
  id = '';
  status = null;
  response = null;

  pk() {
    return this.id;
  }

  static schema = {};
  static fetchOptionsPlugin = options => options;

  // Legacy compatibility hook used by downstream shapes (e.g., LumifyResource)
  static getFetchOptions() {
    return {};
  }

  static async fetchResponse(method, url, body) {
    let options = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
      },
      // Always include cookies (SESSION/JWT/CSRF) for admin + journal calls
      credentials: 'include',
    };

    if (body) options.body = JSON.stringify(body);
    if (this.fetchOptionsPlugin) options = this.fetchOptionsPlugin(options);

    const response = await fetch(url, options);
    if (!response.ok) {
      const error = new Error(response.statusText);
      error.status = response.status;
      error.response = response;
      throw error;
    }
    return response;
  }

  static async fetch(method, url, body) {
    // CSRF-aware fetch with one automatic retry on 401/403.
    // If backend rotated CSRF token or auth cookies, a call may 401/403.
    // Strategy: on 401/403, perform a lightweight auth-refresh GET to CHECK_LOGIN to receive latest cookies, then retry once.
    const attempt = async () => {
      const response = await this.fetchResponse(method, url, body);
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('json') || response.status === 204) {
        return response.text();
      }
      try {
        return await response.json();
      } catch (error) {
        error.status = 400;
        throw error;
      }
    };

    try {
      return await attempt();
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        const CHECK_LOGIN = '/admin/secured/userLoggedIn';
        try {
          await fetch(CHECK_LOGIN, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
        } catch (_) {
          // Ignore refresh failures; we'll still retry once.
        }
        // Retry original request once after refresh
        return await attempt();
      }
      throw err;
    }
  }
}

export default RequestEntity;
